import { MetadataItem } from "@/types/metadata";
import { dakutenMap, handakutenMap } from "@/components/SearchWithVirtualkeyboard";
import { BUILDINGS } from "@/config/constants";

export type Category = "all" | "classroom" | "faculty" | "other";

const normalizeDiacritics = (s: string) => {
  let out = s;
  Object.entries(dakutenMap).forEach(([base, dak]) => {
    if (!dak) return;
    out = out.split(dak).join(base);
  });
  Object.entries(handakutenMap).forEach(([base, hand]) => {
    if (!hand) return;
    out = out.split(hand).join(base);
  });
  return out;
};

const startsWithAnyPart = (text: unknown, lowerCaseSearch: string) => {
  if (text == null) return false;
  let str: string;
  if (typeof text === "string") {
    str = text;
  } else if (typeof text === "number") {
    str = String(text);
  } else if (Array.isArray(text)) {
    str = text.join(" ");
  } else {
    str = String(text);
  }
  const lower = str.toLowerCase();

  if (lower.startsWith(lowerCaseSearch)) return true;
  const lowerNoSpaces = lower.replace(/\s+/g, "");
  if (lowerNoSpaces.startsWith(lowerCaseSearch)) return true;
  const normalizedLower = normalizeDiacritics(lower);
  if (normalizedLower.startsWith(lowerCaseSearch)) return true;
  const normalizedLowerNoSpaces = normalizeDiacritics(lowerNoSpaces);
  if (normalizedLowerNoSpaces.startsWith(lowerCaseSearch)) return true;
  const parts = lower.trim().split(" ").filter(Boolean);
  return parts.some(
    (p) => p.startsWith(lowerCaseSearch) || normalizeDiacritics(p).startsWith(lowerCaseSearch),
  );
};

export function filterItems(
  items: MetadataItem[],
  opts?: { search?: string; category?: Category },
) {
  let list = items.filter((item) => item.show_in_sidebar);

  const category = opts?.category ?? "all";
  if (category === "classroom") {
    list = list.filter((item) => item.category === "classroom");
  } else if (category === "faculty") {
    list = list.filter((item) => item.category === "faculty");
  } else if (category === "other") {
    list = list.filter((item) => item.category !== "classroom" && item.category !== "faculty");
  }

  const search = opts?.search ?? "";
  if (search) {
    const lowerCaseSearch = search.toLowerCase();
    list = list.filter(
      (item) =>
        startsWithAnyPart(item.faculty_name_kana, lowerCaseSearch) ||
        startsWithAnyPart(item.name_kana, lowerCaseSearch) ||
        startsWithAnyPart(item.faculty_name_en, lowerCaseSearch) ||
        startsWithAnyPart(item.name_aliases, lowerCaseSearch),
    );
  }

  return list;
}

export function getCountsByCategory(items: MetadataItem[], search?: string) {
  const filtered = filterItems(items, { search, category: "all" });
  const classroom = filtered.filter((i) => i.category === "classroom").length;
  const faculty = filtered.filter((i) => i.category === "faculty").length;
  const other = filtered.filter(
    (i) => i.category !== "classroom" && i.category !== "faculty",
  ).length;
  const all = filtered.length;
  return { all, classroom, faculty, other } as const;
}

export function groupItemsByFloor(items: MetadataItem[]) {
  const sorted = [...items].sort((a, b) => {
    const buildingCompare = a.building.localeCompare(b.building);
    if (buildingCompare !== 0) return buildingCompare;
    return a.floor - b.floor;
  });

  const groups = new Map<string, MetadataItem[]>();
  const groupKeys: string[] = [];
  for (const item of sorted) {
    const key = `${item.building}-${item.floor}`;
    if (!groups.has(key)) {
      groups.set(key, []);
      groupKeys.push(key);
    }
    groups.get(key)!.push(item);
  }

  return groupKeys.map((key) => {
    const [buildingId, floorNum] = key.split("-");
    const buildingLabel = BUILDINGS.find((b) => b.id === buildingId)?.label || "";
    const label = `${buildingLabel} ${floorNum}階`;
    return {
      key,
      label,
      items: groups.get(key)!,
    };
  });
}
